import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useEffect, useState } from "react";

import { atom, useAtom } from "../app-jotai";
import { getDiagramMuidFromUrl } from "../data";

// Public per-diagram sharing. A workspace member turns an unguessable link on
// for the *current* diagram (view-only or edit) and copies it; the backend
// authorizes token holders and enforces read-only for "view" links.
export const shareDialogAtom = atom<boolean>(false);

interface ShareState {
  shared: boolean;
  shareMode: "view" | "edit" | null;
  shareToken: string | null;
  url: string | null;
}

const jsonFetch = async (url: string, init?: RequestInit) => {
  const res = await fetch(url, init);
  if (!res.ok) {
    throw new Error(`${init?.method ?? "GET"} ${url} → HTTP ${res.status}`);
  }
  return res.json();
};

export const ShareDialog = () => {
  const [open, setOpen] = useAtom(shareDialogAtom);
  const muid = getDiagramMuidFromUrl();
  const [state, setState] = useState<ShareState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    if (!muid) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setState((await jsonFetch(`/api/diagrams/${muid}/share`)) as ShareState);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setMode = async (mode: "view" | "edit") => {
    if (!muid) {
      return;
    }
    setError(null);
    try {
      setState(
        (await jsonFetch(`/api/diagrams/${muid}/share`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ mode }),
        })) as ShareState,
      );
    } catch (err: any) {
      setError(err.message);
    }
  };

  const revoke = async () => {
    if (!muid) {
      return;
    }
    setError(null);
    try {
      setState(
        (await jsonFetch(`/api/diagrams/${muid}/share`, {
          method: "DELETE",
        })) as ShareState,
      );
    } catch (err: any) {
      setError(err.message);
    }
  };

  const copy = async () => {
    if (!state?.url) {
      return;
    }
    try {
      await navigator.clipboard.writeText(state.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked — the input is selectable as a fallback
    }
  };

  if (!open) {
    return null;
  }

  const selectValue = state?.shared ? state.shareMode ?? "view" : "off";

  return (
    <Dialog
      onCloseRequest={() => setOpen(false)}
      title="Share diagram"
      size="small"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          paddingBottom: "1rem",
        }}
      >
        {!muid ? (
          <div style={{ opacity: 0.7 }}>
            Open or create a saved diagram first — sharing is per diagram.
          </div>
        ) : loading ? (
          <div style={{ opacity: 0.6 }}>Loading…</div>
        ) : (
          <>
            <div
              style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
            >
              <span style={{ opacity: 0.6, fontSize: 13 }}>Public link</span>
              <select
                value={selectValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "off") {
                    revoke();
                  } else {
                    setMode(v as "view" | "edit");
                  }
                }}
                style={{ padding: ".25rem .4rem", borderRadius: 6 }}
              >
                <option value="off">Off (private)</option>
                <option value="view">On — view only</option>
                <option value="edit">On — anyone can edit</option>
              </select>
            </div>

            {state?.shared && state.url && (
              <div style={{ display: "flex", gap: ".5rem" }}>
                <input
                  readOnly
                  className="ExcTextField__input"
                  style={{ flex: 1 }}
                  value={state.url}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  className="excalidraw-button"
                  style={{ padding: "0 1rem", fontWeight: 600 }}
                  onClick={copy}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            )}

            <div style={{ opacity: 0.6, fontSize: 12 }}>
              {state?.shared
                ? state.shareMode === "edit"
                  ? "Anyone with the link can view AND edit — no sign-in needed."
                  : "Anyone with the link can view (read-only) — no sign-in needed."
                : "This diagram is private to your workspace."}
            </div>

            {error && (
              <div style={{ color: "var(--color-danger)" }}>{error}</div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
